import z from "zod";

import { InstructionAction } from "@generated/instruction-actions";

import {
  addressSchema,
  dateStandardSchema,
  instructionIdSchema,
  numberSchema,
} from "./misc";

/**
 * helpers
 */

export function assertInstructionAction(
  action: unknown,
): asserts action is InstructionAction {
  if (!instructionActionValues.includes(action as InstructionAction)) {
    throw new Error("Invalid InstructionAction");
  }
}

/**
 * schemas
 */

export const InstructionState = {
  PendingActivation: "PendingActivation",
  Active: "Active",
  PendingDeactivation: "PendingDeactivation",
  Deactivated: "Deactivated",
  Errored: "Errored",
  Completed: "Completed",
  UserNotDelegated: "UserNotDelegated",
} as const;
export type InstructionState =
  (typeof InstructionState)[keyof typeof InstructionState];
export const instructionStateValues = Object.values(InstructionState) as [
  InstructionState,
  ...InstructionState[],
];
export const instructionStateSchema = z.enum(instructionStateValues);

export * from "@generated/instruction-actions";
export const instructionActionValues = Object.values(InstructionAction) as [
  InstructionAction,
  ...InstructionAction[],
];
export const instructionActionSchema = z.enum(instructionActionValues);

export const InstructionScheduleStartType = {
  Now: "now",
  DateTime: "datetime",
} as const;
export type InstructionScheduleStartType =
  (typeof InstructionScheduleStartType)[keyof typeof InstructionScheduleStartType];
export const instructionScheduleStartValues = Object.values(
  InstructionScheduleStartType,
) as [InstructionScheduleStartType, ...InstructionScheduleStartType[]];
const baseStartSchema = z.object({
  type: z.enum(instructionScheduleStartValues),
});
const nowStartSchema = baseStartSchema.extend({
  type: z.literal(InstructionScheduleStartType.Now),
});
const dateTimeStartSchema = baseStartSchema.extend({
  type: z.literal(InstructionScheduleStartType.DateTime),
  startDate: dateStandardSchema,
  startTime: z.string(),
});
export const instructionScheduleStartSchema = z.discriminatedUnion("type", [
  nowStartSchema,
  dateTimeStartSchema,
]);
export type InstructionScheduleStart = z.infer<
  typeof instructionScheduleStartSchema
>;

export const InstructionScheduleRepeatType = {
  UntilDeactivated: "until-deactivated",
  MaxExecutions: "max-executions",
} as const;
export type InstructionScheduleRepeatType =
  (typeof InstructionScheduleRepeatType)[keyof typeof InstructionScheduleRepeatType];
export const instructionScheduleRepeatValues = Object.values(
  InstructionScheduleRepeatType,
) as [InstructionScheduleRepeatType, ...InstructionScheduleRepeatType[]];
const baseRepeatSchema = z.object({});
const untilDeactivatedRepeatSchema = baseRepeatSchema.extend({
  type: z.literal(InstructionScheduleRepeatType.UntilDeactivated),
});
const maxExecutionsRepeatSchema = baseRepeatSchema.extend({
  type: z.literal(InstructionScheduleRepeatType.MaxExecutions),
  maxExecutions: numberSchema,
});
export const instructionScheduleRepeatSchema = z.discriminatedUnion("type", [
  untilDeactivatedRepeatSchema,
  maxExecutionsRepeatSchema,
]);
export type InstructionScheduleRepeat = z.infer<
  typeof instructionScheduleRepeatSchema
>;

export const InstructionScheduleRetryType = {
  UntilDeactivated: "until-deactivated",
  TimeLimited: "time-limited",
} as const;
export type InstructionScheduleRetryType =
  (typeof InstructionScheduleRetryType)[keyof typeof InstructionScheduleRetryType];
export const instructionScheduleRetryValues = Object.values(
  InstructionScheduleRetryType,
) as [InstructionScheduleRetryType, ...InstructionScheduleRetryType[]];
const baseRetrySchema = z.object({});
const untilDeactivatedRetrySchema = baseRetrySchema.extend({
  type: z.literal(InstructionScheduleRetryType.UntilDeactivated),
});
const timeLimitedRetrySchema = baseRetrySchema.extend({
  type: z.literal(InstructionScheduleRetryType.TimeLimited),
  retryCount: numberSchema,
});
export const instructionScheduleRetrySchema = z.discriminatedUnion("type", [
  untilDeactivatedRetrySchema,
  timeLimitedRetrySchema,
]);
export type InstructionScheduleRetry = z.infer<
  typeof instructionScheduleRetrySchema
>;

export const InstructionViewTimescale = {
  Days: "days",
  Hours: "hours",
  Minutes: "minutes",
  Seconds: "seconds",
} as const;
export type InstructionViewTimescale =
  (typeof InstructionViewTimescale)[keyof typeof InstructionViewTimescale];
export const instructionViewTimescaleValues = Object.values(
  InstructionViewTimescale,
) as [InstructionViewTimescale, ...InstructionViewTimescale[]];

//

export const instructionExecutionStateSchema = z.object({
  executionStatus: instructionStateSchema,
  executionCounter: z.number().int(),
  lastExecuted: z.number().int(),
});

export const instructionSchema = z.object({
  instructionId: instructionIdSchema,
  startAt: z.string().nullish(),
  interval: z.number().nullish(),
  timeout: z.number().nullish(),
  maxExecutions: z.number().nullish(),
  executionState: instructionExecutionStateSchema,
  actionName: z.string().optional(),
  arguments: z.any(),
  nickname: z.string().nullish(),
  lastInstructedValue: z.string(),
  totalInstructedValue: z.string(),
  instructedTokenAddress: addressSchema.nullish(),
});
export type Instruction = z.infer<typeof instructionSchema>;

export const instructionsSchema = z.array(instructionSchema);
export type Instructions = z.infer<typeof instructionsSchema>;

export const instructionSummarySchema = z.object({
  instructionId: instructionIdSchema,
  chainId: z.number().int(),
  executionCount: z.number().int(),
  onTimeCount: z.number().int(),
  lateCount: z.number().int(),
  wasActivatedDuring: z.boolean(),
  amount: z.number().default(0),
  instructedValue: z.string().nullish().default("0"),
  instructedTokenAddress: z.string().nullish().default(null),
});
export type InstructionSummary = z.infer<typeof instructionSummarySchema>;

export const instructionSummariesSchema = z.array(instructionSummarySchema);
export type InstructionSummaries = z.infer<typeof instructionSummariesSchema>;

export const instructionSummariesByDateSchema = z.record(
  instructionSummariesSchema,
);
export type InstructionSummariesByDate = z.infer<
  typeof instructionSummariesByDateSchema
>;
